import {
  formatShortDateAndTimeOrSameDay,
  IntegrationProvider,
  IntegrationRunStatus,
  useCustomQuery,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { tokens } from '../crop-circle/tokens';
import { ActivityCard } from '../crop-circle/Components/ActivityCard';
import { INTEGRATION_PROVIDER_NAMES } from './integrationAttributionTag';

/**
 * Per-run detail (Plan 0019). The unbounded per-asset breakdown is NOT on the
 * Mongo record — it lives in a `details.json` blob, fetched on demand via
 * `integration.runDetails`. Namespaced, so read `data.integration.runDetails`.
 */
const RUN_DETAILS = `
  query IntegrationRunDetails($id: ID!) {
    integration {
      runDetails(id: $id)
    }
  }
`;

/** Per-asset rollup, from the run's `details.json` blob (Plan 0019). */
export interface IntegrationRunAsset {
  assetId: string;
  assetName: string | null;
  readingsWritten: number;
  issueCount: number;
}

/** Aggregate counts persisted on `IntegrationRun.counts` (the worker's
 * SyncCounts). Optional/loose because it is opaque JSON on the record. */
export interface IntegrationRunCounts {
  assetsMatched?: number;
  readingsWritten?: number;
  issuesOpened?: number;
  issuesUpdated?: number;
  faultsLampLit?: number;
}

/**
 * One `IntegrationRun` as returned by `findIntegrationRuns`, projected
 * to the fields the feed card needs. `assets` is NOT part of the feed record —
 * it's the blob-sourced detail (via `integration.runDetails`), present only when
 * a consumer has fetched it (mobile expand / web panel).
 */
export interface IntegrationRunData {
  id: string;
  provider: IntegrationProvider;
  /** ISO timestamp the run executed — its position in the chronological feed. */
  ranAt: string;
  status: IntegrationRunStatus | null;
  counts: IntegrationRunCounts | null;
  assets?: IntegrationRunAsset[] | null;
}

type RunDetailsResponse = {
  integration?: {
    runDetails?: { assets?: IntegrationRunAsset[] | null } | null;
  };
};

export interface IntegrationRunCardProps {
  run: IntegrationRunData;
  /**
   * Name shown in the gray pill slot — the workspace name, matching sibling
   * rows (`asset.name || workspace.name`). A run is a workspace-wide event with
   * no single asset. Omitted ⇒ no pill.
   */
  workspaceName?: string;
  borderRadius?: number;
  /**
   * When provided (web), tapping the card calls this instead of expanding in
   * place — the consumer opens a right-hand detail panel. When omitted (mobile),
   * the card expands downward to show the per-asset breakdown inline.
   */
  onPress?: () => void;
  /**
   * Selected-state styling when a detail panel is open for this run (web). Only
   * consulted when `onPress` is set; inline mode uses its own expanded state.
   */
  isSelected?: boolean;
}

/**
 * Feed row for a single integration sync run, placed chronologically in the
 * activity list at its `ranAt` (Plan 0019 — replaces the pinned daily digest).
 *
 * - Blue `'routines'` variant normally; yellow `'blocker'` on a FAILED run.
 * - Title is plain text with counts inlined (no pills): "GeoTab synced N assets
 *   · M readings · K faults". The fault segment drops when no faults were
 *   touched. A run that changed nothing (decision 1: still recorded + shown)
 *   reads "GeoTab synced N assets · no changes".
 * - Tapping expands in place to a per-asset breakdown (from `assets`), mirroring
 *   the digest's detail. A run with no per-asset rows (e.g. a no-op) is not
 *   expandable — it renders as a plain, non-interactive row.
 */
export const IntegrationRunCard = (props: IntegrationRunCardProps) => {
  const { t } = useTranslation();
  const { run } = props;
  const [expanded, setExpanded] = useState(false);

  const providerName = INTEGRATION_PROVIDER_NAMES[run.provider] ?? run.provider;
  const formattedDate = run.ranAt
    ? formatShortDateAndTimeOrSameDay(run.ranAt)
    : '';

  // Compare via the enum's string value so it stays correct even when a mocked
  // module spread hasn't initialised the enum object before render.
  const isFailed = (run.status as string) === 'FAILED';

  const assetsSynced = run.counts?.assetsMatched ?? 0;
  const readingsWritten = run.counts?.readingsWritten ?? 0;
  const faults =
    (run.counts?.issuesOpened ?? 0) + (run.counts?.issuesUpdated ?? 0);
  const hasChanges = readingsWritten > 0 || faults > 0;

  // Web opens a side panel (onPress); mobile expands the breakdown inline.
  const usesExternalDetail = !!props.onPress;
  const selected = usesExternalDetail ? !!props.isSelected : expanded;
  // Web cards are always tappable (open the panel, even for a no-op summary);
  // inline mobile cards are tappable only when the run changed something (so a
  // no-op run isn't a dead tap that fetches an empty breakdown).
  const isExpandable = hasChanges;
  const tappable = usesExternalDetail || isExpandable;

  // Mobile: the per-asset breakdown lives in a blob, fetched lazily only once
  // the card is expanded (web fetches it in the detail panel instead).
  const { data: detailsData, isFetching: detailsFetching } = useCustomQuery({
    query: RUN_DETAILS,
    variables: { id: run.id },
    cacheKey: ['integrationRunDetails', run.id],
    options: { enabled: !usesExternalDetail && expanded },
  }) as { data?: RunDetailsResponse; isFetching: boolean };
  const assets = detailsData?.integration?.runDetails?.assets ?? [];

  // "GeoTab synced N assets" is always the lead segment. Then either the change
  // counts, or a "no changes" tail for a no-op run (still shown per decision 1).
  const titleSegments = [
    t('integrations.digest.titleSegment', {
      provider: providerName,
      count: assetsSynced,
    }),
    ...(hasChanges
      ? [
          t('integrations.digest.readingsCount', { count: readingsWritten }),
          ...(faults > 0
            ? [t('integrations.digest.faultsCount', { count: faults })]
            : []),
        ]
      : [t('integrations.run.noChanges', 'no changes')]),
  ];
  const title = titleSegments.join(' · ');

  const description = isFailed
    ? t('integrations.digest.failedDescription')
    : undefined;

  const card = (
    <ActivityCard
      assetName={props.workspaceName ?? ''}
      showAssetName={!!props.workspaceName}
      title={title}
      date={formattedDate}
      description={description}
      severityVariant={isFailed ? 'blocker' : 'routines'}
      isSelected={selected}
      borderRadius={props.borderRadius}
    />
  );

  return (
    <View>
      {tappable ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('integrations.run.accessibilityLabel', {
            provider: providerName,
            date: formattedDate,
          })}
          onPress={
            usesExternalDetail
              ? props.onPress
              : () => setExpanded((prev) => !prev)
          }
        >
          {card}
        </Pressable>
      ) : (
        card
      )}

      {/* Inline (mobile) breakdown only — web shows it in the side panel. The
          per-asset rows arrive from the details blob after expand. When the blob
          is missing/empty (or still loading), show an explicit line rather than
          an empty expanded card that gives no feedback on tap. */}
      {!usesExternalDetail && expanded && (
        <View style={styles.breakdown}>
          {assets.length > 0 ? (
            assets.map((asset) => (
              <View key={asset.assetId} style={styles.assetRow}>
                <Text style={styles.assetName} numberOfLines={1}>
                  {asset.assetName || asset.assetId}
                </Text>
                <Text style={styles.assetMeta}>
                  {t('integrations.digest.readingsCount', {
                    count: asset.readingsWritten,
                  })}
                  {asset.issueCount > 0
                    ? ` · ${t('integrations.digest.faultsCount', {
                        count: asset.issueCount,
                      })}`
                    : ''}
                </Text>
              </View>
            ))
          ) : (
            <Text style={styles.assetMeta}>
              {detailsFetching
                ? t('loading', 'Loading…')
                : t('integrations.run.detailEmpty')}
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

IntegrationRunCard.displayName = 'IntegrationRunCard';

const styles = StyleSheet.create({
  breakdown: {
    paddingLeft: tokens.spacing.lg,
    paddingRight: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
    gap: tokens.spacing.xs,
  },
  assetRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  assetName: {
    ...tokens.typography.body.small,
    color: tokens.colors.neutral[800],
    flexShrink: 1,
  },
  assetMeta: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[600],
  },
});
